<script setup>
import EmptyState from '@/components/EmptyState';
import { ref, inject } from 'vue';
import { useCustomerStore } from '@/stores/customer';
import { storeToRefs } from 'pinia';

const customerStore = useCustomerStore();
const { search } = customerStore;
const { all } = storeToRefs(customerStore);

const params = ref({
    term: '',
    status: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchCustomers = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchCustomers();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchCustomers();
};

const openModal = (modal, mode = 'Create', customer = null) => {
    modals.show(modal, {
        props: customer ? {
            customer: customer,
            mode: mode
        } : {
            mode: mode,
        }
    }).then(() => {
        searchCustomers();
    }).catch(() => {
        // Modal cancelled
    });
};
</script>

<template>
<MainContentLayout>
    <template #title>{{ $t('label.customer_management') }}</template>
    <template #actions>
        <StyledButton @click="openModal('CustomersModal')"><FontAwesomeIcon icon="plus" class="mr-1" />
            {{ $t('label.new_customer') }}
        </StyledButton>
    </template>
    <div class="rounded-xl border border-line bg-surface p-4 mb-4">
        <div class="grid lg:grid-cols-3 gap-x-3">
            <StyledSearch
                v-model="params.term"
                v-debounce:500ms="searchCustomers"
                name="search"
                :label="$t('label.search')"
                :debounce-events="['input']"
                @click:clear="clearSearch" />
            <StyledDropdown
                v-model="params.status"
                clearable
                :placeholder="$t('label.status')"
                :options="[
                    { label: $t('label.active'), value: true },
                    { label: $t('label.inactive'), value: false },
                ]"
                name="status"
                @input="searchCustomers" />
        </div>
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.name'), key: 'name', value: item => item.name, sortable: false },
            { title: $t('label.email'), key: 'email', value: item => item.email ?? '', sortable: false },
            { title: $t('label.phone'), key: 'phone', value: item => item.phone ?? '', sortable: false },
            { title: $t('label.vehicles'), key: 'vehicles_count', value: item => item.vehicles_count, sortable: false },
            { title: $t('label.status'), key: 'status', value: item => item.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
        ]"
        :actions="[
            { title: $t('title.edit'), icon: 'mdi-pencil-outline', action: (item) => openModal('CustomersModal', 'Edit', item) },
        ]"
        @table-update="tableUpdate">
        <template #[`item.name`]="{ item }">
            <span class="flex items-center gap-2">
                <span class="font-semibold">{{ item.name }}</span>
                <StatusChip
                    v-if="item.is_company"
                    color="info">{{ $t('label.company') }}</StatusChip>
            </span>
        </template>
        <template #[`item.status`]="{ item }">
            <StatusChip :color="item.is_active ? 'success' : 'neutral'">
                {{ item.is_active ? $t('label.active') : $t('label.inactive') }}
            </StatusChip>
        </template>
        <template #no-data>
            <EmptyState
                icon="mdi-account-multiple-outline"
                :title="$t('label.no_customers_found')"
                :hint="$t('label.no_customers_hint')">
                <StyledButton @click="openModal('CustomersModal')"><FontAwesomeIcon icon="plus" class="mr-1" />
                    {{ $t('label.new_customer') }}
                </StyledButton>
            </EmptyState>
        </template>
    </DataTable>
</MainContentLayout>
</template>
